import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Outlet } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { onboardingAtom, sidebarCollapsedAtom } from "@/store";
import { GettingStartedDialog } from "@/components/common";
import { Header } from "./Header";
import { Navigation } from "./Navigation";

const NAV_WIDTH_EXPANDED = "240px";
const NAV_WIDTH_COLLAPSED = "64px";
const HEADER_HEIGHT = "56px";

const useStyles = makeStyles({
  layout: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
  },
  main: {
    flex: 1,
    overflow: "hidden",
    marginTop: HEADER_HEIGHT,
    backgroundColor: tokens.colorNeutralBackground3,
    transitionProperty: "margin-inline-start",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease",
  },
  mainExpanded: {
    marginInlineStart: NAV_WIDTH_EXPANDED,
  },
  mainCollapsed: {
    marginInlineStart: NAV_WIDTH_COLLAPSED,
  },
});

export function Layout() {
  const navCollapsed = useAtomValue(sidebarCollapsedAtom);
  const styles = useStyles();
  const [onboarding, setOnboarding] = useAtom(onboardingAtom);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    const stored = localStorage.getItem("priorityos-onboarding");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.dismissed) {
          setHydrated(true);
          return;
        }
      } catch {
        /* empty */
      }
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated && !onboarding.dismissed && !shownRef.current) {
      shownRef.current = true;
      setOnboardingOpen(true);
    }
  }, [hydrated, onboarding.dismissed]);

  return (
    <div className={styles.layout}>
      <Header />
      <Navigation />
      <main
        className={mergeClasses(
          styles.main,
          navCollapsed ? styles.mainCollapsed : styles.mainExpanded,
        )}
      >
        <Outlet />
      </main>
      <GettingStartedDialog
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onDismissForever={() =>
          setOnboarding((prev) => ({ ...prev, dismissed: true }))
        }
      />
    </div>
  );
}
